import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import type { Address } from 'viem';

import { useGetSwapQuote, useSupply, useSwapTokensAndSupply } from 'clients/api';
import {
  Delimiter,
  LabeledInlineContent,
  NoticeError,
  NoticeWarning,
  SelectTokenTextField,
  SpendingLimit,
  Toggle,
  TokenTextField,
  TransactionQueue,
} from 'components';
import { NULL_ADDRESS } from 'constants/address';
import { SwitchChainNotice } from 'containers/SwitchChainNotice';
import { useCollateral } from 'hooks/useCollateral';
import useDebounceValue from 'hooks/useDebounceValue';
import { useGetContractAddress } from 'hooks/useGetContractAddress';
import { useGetUserSlippageTolerance } from 'hooks/useGetUserSlippageTolerance';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import useTokenApproval from 'hooks/useTokenApproval';
import { useAnalytics } from 'libs/analytics';
import { VError, handleError } from 'libs/errors';
import { useTranslation } from 'libs/translations';
import { useAccountAddress, useAccountChainId, useChainId } from 'libs/wallet';
import type { Asset, Pool, TokenBalance } from 'types';
import {
  areTokensEqual,
  convertMantissaToTokens,
  convertTokensToMantissa,
  formatTokensToReadableValue,
  getSwapToTokenAmount,
  isCollateralActionDisabled,
} from 'utilities';
import { Footer } from '../Footer';
import type { Approval } from '../Footer/types';
import { calculateAmountDollars } from '../calculateAmountDollars';
import { getMarketCapabilities } from '../shared/getMarketCapabilities';
import { useGetVTokenWrapperKind } from '../shared/useGetVTokenWrapperKind';
import { useGetOperationFormTokenBalances } from '../useGetOperationFormTokenBalances';
import TEST_IDS from './testIds';
import { useBittensorSupplyQueue } from './useBittensorSupplyQueue';
import { useBundledBorrowLimits } from './useBundledBorrowLimits';
import useForm, { type FormValues } from './useForm';
import {
  type OpenPositionSupplySource,
  useRouterOpenPositionQueue,
} from './useRouterOpenPositionQueue';
import { useRouterSupplyQueue } from './useRouterSupplyQueue';
import { useSupplySimulation } from './useSupplySimulation';

export interface SupplyFormProps {
  asset: Asset;
  pool: Pool;
}

const SupplyForm: React.FC<SupplyFormProps> = ({ asset, pool }) => {
  const { t } = useTranslation();
  const { captureAnalyticEvent } = useAnalytics();

  const { chainId: accountChainId } = useAccountChainId();
  const { chainId } = useChainId();

  const { accountAddress } = useAccountAddress();
  const isUserConnected = !!accountAddress;

  const isWrapUnwrapNativeTokenEnabled = useIsFeatureEnabled({
    name: 'wrapUnwrapNativeToken',
  });
  const isIntegratedSwapEnabled = useIsFeatureEnabled({
    name: 'integratedSwap',
  });
  const isPositionRouterEnabled = useIsFeatureEnabled({
    name: 'positionRouter',
  });
  const [useLegacyBittensorFlow, setUseLegacyBittensorFlow] = useState(false);
  const [bundleBorrowEnabled, setBundleBorrowEnabled] = useState(false);
  const [bundleBorrowAmountTokens, setBundleBorrowAmountTokens] = useState('');
  const isIntegratedSwapFeatureEnabled =
    isIntegratedSwapEnabled &&
    // Check swap and supply action is enabled for underlying token
    !asset.disabledTokenActions.includes('swapAndSupply');

  const { address: nativeTokenGatewayContractAddress } = useGetContractAddress({
    name: 'NativeTokenGateway',
    poolComptrollerContractAddress: pool.comptrollerAddress,
  });

  const { address: swapRouterV2ContractAddress } = useGetContractAddress({
    name: 'SwapRouterV2',
  });

  const { toggleCollateral } = useCollateral();

  const canWrapNativeToken =
    isWrapUnwrapNativeTokenEnabled && !!asset.vToken.underlyingToken.tokenWrapped;

  // Intentionally NOT gated on the positionRouter flag: useGetOperationFormTokenBalances needs wrapperKind for native TAO balance display and native alpha supply sources, which exist independently of the router flow.
  const { data: supplyAssetWrapperKind } = useGetVTokenWrapperKind(
    { underlyingAddress: asset.vToken.underlyingToken.address },
    { enabled: !!accountAddress },
  );
  const supplyAssetCapabilities = getMarketCapabilities({
    underlyingToken: asset.vToken.underlyingToken,
    wrapperKind: supplyAssetWrapperKind ?? 'unsupported',
  });

  const { tokenBalances, userWalletNativeTokenBalanceTokens, bittensorNativeSupplySources } =
    useGetOperationFormTokenBalances({
      poolComptrollerContractAddress: pool.comptrollerAddress,
      accountAddress,
      underlyingToken: asset.vToken.underlyingToken,
      wrapperKind: supplyAssetWrapperKind ?? 'unsupported',
      isIntegratedSwapFeatureEnabled,
      canWrapNativeToken,
      action: 'supply',
    });

  const shouldSelectNativeToken =
    canWrapNativeToken && userWalletNativeTokenBalanceTokens?.gt(asset.userWalletBalanceTokens);

  const supplySourceSelection = useMemo(() => {
    if (bittensorNativeSupplySources.length === 0 || isIntegratedSwapFeatureEnabled) {
      return undefined;
    }

    const nativeAlphaBalance = tokenBalances.find((tokenBalance) =>
      bittensorNativeSupplySources.some((source) =>
        areTokensEqual(source.token, tokenBalance.token),
      ),
    );

    if (!nativeAlphaBalance) {
      return undefined;
    }

    const wrappedBalance = tokenBalances.find((tokenBalance) =>
      areTokensEqual(tokenBalance.token, asset.vToken.underlyingToken),
    );

    const orderedSources = [nativeAlphaBalance, wrappedBalance].filter(
      (tokenBalance): tokenBalance is TokenBalance => !!tokenBalance,
    );
    const sourcesWithBalance = orderedSources.filter((tokenBalance) =>
      tokenBalance.balanceMantissa.isGreaterThan(0),
    );

    return sourcesWithBalance.length > 0 ? sourcesWithBalance : [nativeAlphaBalance];
  }, [
    tokenBalances,
    bittensorNativeSupplySources,
    isIntegratedSwapFeatureEnabled,
    asset.vToken.underlyingToken,
  ]);

  const displayedTokenBalances = supplySourceSelection ?? tokenBalances;
  const defaultSupplySourceToken = supplySourceSelection?.[0]?.token;

  const initialFormValues: FormValues = useMemo(
    () => ({
      amountTokens: '',
      fromToken:
        defaultSupplySourceToken ??
        (shouldSelectNativeToken && asset.vToken.underlyingToken.tokenWrapped
          ? asset.vToken.underlyingToken.tokenWrapped
          : asset.vToken.underlyingToken),
      acknowledgeHighPriceImpact: false,
    }),
    [defaultSupplySourceToken?.address, asset.vToken.underlyingToken, shouldSelectNativeToken],
  );

  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  // Reset form when user disconnects their wallet
  useEffect(() => {
    if (!accountAddress) {
      setFormValues(initialFormValues);
    }
  }, [accountAddress, initialFormValues]);

  // Reset form when initial values change, which indicates the base asset was changed
  useEffect(() => {
    setFormValues(initialFormValues);
  }, [initialFormValues]);

  // a user is trying to wrap the chain's native token if
  // 1) the wrap/unwrap feature is enabled
  // 2) the selected form token is the native token
  // 3) the market's underlying token wraps the native token
  const isWrappingNativeToken = canWrapNativeToken && !!formValues.fromToken.isNative;

  const selectedBittensorNativeSupplySource = bittensorNativeSupplySources.find((source) =>
    areTokensEqual(source.token, formValues.fromToken),
  );
  const isUsingBittensorNativeAlphaSource = !!selectedBittensorNativeSupplySource;
  const isUsingBittensorNativeTaoSource =
    !!formValues.fromToken.isNative &&
    supplyAssetCapabilities.canUseNativeTao &&
    !!asset.vToken.underlyingToken.tokenWrapped &&
    areTokensEqual(formValues.fromToken, asset.vToken.underlyingToken.tokenWrapped);
  const isUsingBittensorNativeSupplySource =
    isUsingBittensorNativeAlphaSource || isUsingBittensorNativeTaoSource;

  const isUsingSwap =
    isIntegratedSwapFeatureEnabled &&
    !isWrappingNativeToken &&
    !isUsingBittensorNativeSupplySource &&
    !areTokensEqual(asset.vToken.underlyingToken, formValues.fromToken);

  let spenderAddress: undefined | Address = asset.vToken.address;

  if (isUsingBittensorNativeSupplySource) {
    spenderAddress = undefined;
  } else if (isWrappingNativeToken) {
    spenderAddress = nativeTokenGatewayContractAddress;
  } else if (isUsingSwap) {
    spenderAddress = swapRouterV2ContractAddress;
  }

  const {
    isTokenApproved: isFromTokenApproved,
    isApproveTokenLoading: isApproveFromTokenLoading,
    walletSpendingLimitTokens: fromTokenWalletSpendingLimitTokens,
    revokeWalletSpendingLimit: revokeFromTokenWalletSpendingLimit,
    isRevokeWalletSpendingLimitLoading: isRevokeFromTokenWalletSpendingLimitLoading,
  } = useTokenApproval({
    token: formValues.fromToken,
    spenderAddress,
    accountAddress,
  });

  const {
    execute: executeBittensorSupplyQueue,
    isRunning: isBittensorSupplyQueueRunning,
    items: bittensorSupplyQueueItems,
  } = useBittensorSupplyQueue();
  const {
    execute: executeRouterSupplyQueue,
    isRunning: isRouterSupplyQueueRunning,
    items: routerSupplyQueueItems,
  } = useRouterSupplyQueue();
  const {
    execute: executeRouterOpenPositionQueue,
    isRunning: isRouterOpenPositionQueueRunning,
    items: routerOpenPositionQueueItems,
  } = useRouterOpenPositionQueue();

  const useRouterFlow = isPositionRouterEnabled && !useLegacyBittensorFlow;

  // Bundled openPosition is limited to native-borrow-capable markets. We
  // find the first borrowable candidate and let the capability adapter decide
  // whether it can surface the bundle toggle.
  const borrowTargetCandidate = useMemo(
    () =>
      pool.assets.find(
        (a) => !a.disabledTokenActions.includes('borrow') && a.borrowCapTokens.isGreaterThan(0),
      ),
    [pool.assets],
  );
  const { data: borrowTargetWrapperKind } = useGetVTokenWrapperKind(
    {
      underlyingAddress: borrowTargetCandidate?.vToken.underlyingToken.address ?? NULL_ADDRESS,
    },
    { enabled: !!borrowTargetCandidate && isPositionRouterEnabled },
  );
  const borrowTargetCapabilities = borrowTargetCandidate
    ? getMarketCapabilities({
        underlyingToken: borrowTargetCandidate.vToken.underlyingToken,
        wrapperKind: borrowTargetWrapperKind ?? 'unsupported',
      })
    : undefined;
  const borrowTargetAsset = borrowTargetCapabilities?.canRouteNativeBorrow
    ? borrowTargetCandidate
    : undefined;
  const canBundleOpenPosition =
    isPositionRouterEnabled &&
    isUsingBittensorNativeSupplySource &&
    asset.userCollateralFactor > 0 &&
    !!borrowTargetAsset &&
    useRouterFlow;

  const { mutateAsync: supply, isPending: isSupplyLoading } = useSupply();

  const { mutateAsync: swapExactTokensForTokensAndSupply, isPending: isSwapAndSupplyLoading } =
    useSwapTokensAndSupply({
      poolName: pool.name,
      poolComptrollerAddress: pool.comptrollerAddress,
      vToken: asset.vToken,
      isSwappingNative: !!formValues.fromToken.isNative,
    });

  const isSubmitting =
    isSupplyLoading ||
    isSwapAndSupplyLoading ||
    isBittensorSupplyQueueRunning ||
    isRouterSupplyQueueRunning ||
    isRouterOpenPositionQueueRunning;

  const onSubmit = async () => {
    const amountMantissa = convertTokensToMantissa({
      value: new BigNumber(formValues.amountTokens.trim()),
      token: formValues.fromToken,
    });

    if (isUsingBittensorNativeSupplySource) {
      if (
        canBundleOpenPosition &&
        bundleBorrowEnabled &&
        Number(bundleBorrowAmountTokens) > 0 &&
        borrowTargetAsset
      ) {
        const supplySource: OpenPositionSupplySource = selectedBittensorNativeSupplySource
          ? {
              kind: 'native-alpha',
              source: selectedBittensorNativeSupplySource,
            }
          : { kind: 'native-tao' };
        const borrowAmountMantissa = BigInt(
          convertTokensToMantissa({
            value: new BigNumber(bundleBorrowAmountTokens),
            token: borrowTargetAsset.vToken.underlyingToken,
          }).toFixed(),
        );
        return executeRouterOpenPositionQueue({
          supplyAmountMantissa: BigInt(amountMantissa.toFixed()),
          supplySource,
          supplyVToken: asset.vToken,
          borrowAmountMantissa,
          borrowVToken: borrowTargetAsset.vToken,
          borrowUnwrapNative: true,
          slippagePercentage: userSlippageTolerancePercentage,
        });
      }
      if (useRouterFlow) {
        return executeRouterSupplyQueue({
          amountMantissa: BigInt(amountMantissa.toFixed()),
          nativeAlphaSource: selectedBittensorNativeSupplySource,
          slippagePercentage: userSlippageTolerancePercentage,
          vToken: asset.vToken,
        });
      }
      return executeBittensorSupplyQueue({
        amountMantissa: BigInt(amountMantissa.toFixed()),
        nativeAlphaSource: selectedBittensorNativeSupplySource,
        slippagePercentage: userSlippageTolerancePercentage,
        vToken: asset.vToken,
      });
    }

    if (!isUsingSwap && !isWrappingNativeToken) {
      return supply({
        amountMantissa,
        poolName: pool.name,
        vToken: asset.vToken,
      });
    }

    if (isWrappingNativeToken) {
      return supply({
        poolName: pool.name,
        vToken: asset.vToken,
        amountMantissa,
        wrap: true,
        poolComptrollerContractAddress: pool.comptrollerAddress,
      });
    }

    // Throw an error if we're meant to execute a swap but no swap was
    // passed through props. This should never happen since the form is
    // disabled while swap infos are being fetched, but we add this logic
    // as a safeguard
    if (!swapQuote) {
      throw new VError({ type: 'unexpected', code: 'somethingWentWrong' });
    }

    return swapExactTokensForTokensAndSupply({
      swapQuote,
    });
  };

  const debouncedFormAmountTokens = useDebounceValue(formValues.amountTokens);
  const fromTokenAmountTokens = new BigNumber(debouncedFormAmountTokens || 0);

  const { userSlippageTolerancePercentage } = useGetUserSlippageTolerance();
  const {
    data: getSwapQuoteData,
    error: getSwapQuoteError,
    isLoading: isGetSwapQuoteLoading,
  } = useGetSwapQuote(
    {
      fromToken: formValues.fromToken,
      fromTokenAmountTokens,
      toToken: asset.vToken.underlyingToken,
      direction: 'exact-in',
      recipientAddress: swapRouterV2ContractAddress || NULL_ADDRESS,
      slippagePercentage: userSlippageTolerancePercentage,
    },
    {
      enabled:
        isUsingSwap && !!swapRouterV2ContractAddress && fromTokenAmountTokens.isGreaterThan(0),
    },
  );
  const swapQuote = getSwapQuoteData?.swapQuote;

  const approval: Approval | undefined = spenderAddress
    ? {
        type: 'token',
        token: formValues.fromToken,
        spenderAddress,
      }
    : undefined;

  const fromTokenUserWalletBalanceTokens = useMemo(() => {
    const tokenBalance = tokenBalances.find((item) =>
      areTokensEqual(item.token, formValues.fromToken),
    );

    return (
      tokenBalance &&
      convertMantissaToTokens({
        value: tokenBalance.balanceMantissa,
        token: tokenBalance.token,
      })
    );
  }, [tokenBalances, formValues.fromToken]);

  // Determine the amount of tokens the user can supply, taking the supply cap, their wallet
  // balance and spending limit in consideration
  const limitTokens = useMemo(() => {
    // If user is using swap, we fill the input with the user's wallet balance as we don't have a
    // way to know in advance exactly how much of the fromToken they can supply to
    let amountTokens = new BigNumber(fromTokenUserWalletBalanceTokens || 0);

    // If user has set a spending limit for fromToken, then we take it in consideration
    if (fromTokenWalletSpendingLimitTokens?.isGreaterThan(0)) {
      amountTokens = BigNumber.min(amountTokens, fromTokenWalletSpendingLimitTokens);
    }

    // Take supply cap in consideration, if user is not swapping
    if (!isUsingSwap) {
      const marginWithSupplyCapTokens = asset.supplyCapTokens.isEqualTo(0)
        ? new BigNumber(0)
        : asset.supplyCapTokens.minus(asset.supplyBalanceTokens);

      // The supply cap margin is wrapper-denominated, but a native-alpha source
      // input is native-alpha-denominated. Convert the margin into native-alpha
      // units before the min so MAX caps at the right native amount at
      // non-parity rates: nativeTokens = wrapperTokens × exchangeRate /
      // 10^nativeDecimals (same direction as the repay debt conversion).
      // ROUND_DOWN so the converted cap is never exceeded once re-wrapped.
      const nativeMarginWithSupplyCapTokens =
        selectedBittensorNativeSupplySource && selectedBittensorNativeSupplySource.exchangeRate > 0n
          ? marginWithSupplyCapTokens
              .times(selectedBittensorNativeSupplySource.exchangeRate.toString())
              .dividedBy(new BigNumber(10).pow(selectedBittensorNativeSupplySource.token.decimals))
              .dp(selectedBittensorNativeSupplySource.token.decimals, BigNumber.ROUND_DOWN)
          : marginWithSupplyCapTokens;

      amountTokens = BigNumber.min(amountTokens, nativeMarginWithSupplyCapTokens);
    }

    return amountTokens;
  }, [
    isUsingSwap,
    asset.supplyBalanceTokens,
    fromTokenUserWalletBalanceTokens,
    fromTokenWalletSpendingLimitTokens,
    asset.supplyCapTokens,
    selectedBittensorNativeSupplySource,
  ]);

  let toTokenAmountTokens =
    isUsingSwap && swapQuote ? getSwapToTokenAmount(swapQuote) : debouncedFormAmountTokens;
  toTokenAmountTokens = new BigNumber(toTokenAmountTokens || 0);

  const bundleBorrowLegActive =
    canBundleOpenPosition &&
    bundleBorrowEnabled &&
    Number(bundleBorrowAmountTokens) > 0 &&
    !!borrowTargetAsset;

  const { balanceMutations, simulatedPool, supplyOnlySimulatedPool } = useSupplySimulation({
    asset,
    borrowTargetAsset,
    bundleBorrowAmountTokens,
    bundleBorrowLegActive,
    formValues,
    isUsingBittensorNativeSupplySource,
    pool,
    toTokenAmountTokens,
    userSlippageTolerancePercentage,
  });

  const {
    borrowTargetAvailableTokens,
    bundleBorrowAmountMissing,
    bundleBorrowExceedsCash,
    bundleBorrowExceedsLimit,
    bundleBorrowExceedsSafeMax,
    handleBundleBorrowSafeMaxClick,
    maxBundledBorrowTokens,
    safeMaxBundledBorrowTokens,
  } = useBundledBorrowLimits({
    borrowTargetAsset,
    bundleBorrowAmountTokens,
    bundleBorrowEnabled,
    bundleBorrowLegActive,
    canBundleOpenPosition,
    pool,
    setBundleBorrowAmountTokens,
    supplyOnlySimulatedPool,
  });

  const { handleSubmit, isFormValid, formError } = useForm({
    asset,
    simulatedPool,
    balanceMutations,
    fromTokenUserWalletBalanceTokens,
    pool,
    fromTokenWalletSpendingLimitTokens,
    isFromTokenApproved,
    swapQuote,
    swapQuoteErrorCode: getSwapQuoteError?.code,
    onSubmit,
    formValues,
    setFormValues,
  });

  const readableFromTokenUserWalletBalanceTokens = formatTokensToReadableValue({
    value: fromTokenUserWalletBalanceTokens,
    token: formValues.fromToken,
  });

  const isAccountOnWrongChain = accountChainId !== chainId;
  const isCollateralToggleVisible = asset.userCollateralFactor > 0;
  const isCollateralToggleDisabled = isCollateralActionDisabled({
    disabledTokenActions: asset.disabledTokenActions,
    isCollateralOfUser: asset.isCollateralOfUser,
  });

  const captureAmountSetAnalyticEvent = ({
    amountTokens,
    maxSelected,
  }: {
    amountTokens: BigNumber | string;
    maxSelected: boolean;
  }) => {
    if (Number(formValues.amountTokens) > 0) {
      captureAnalyticEvent(
        'supply_amount_set',
        {
          poolName: pool.name,
          assetSymbol: asset.vToken.underlyingToken.symbol,
          usdAmount: calculateAmountDollars({
            amountTokens,
            tokenPriceCents: asset.tokenPriceCents,
          }),
          maxSelected,
        },
        {
          debounced: true,
        },
      );
    }
  };

  const handleToggleCollateral = async () => {
    try {
      await toggleCollateral({
        asset,
        poolName: pool.name,
        comptrollerAddress: pool.comptrollerAddress,
      });
    } catch (error) {
      handleError({ error });
    }
  };

  const handleRightMaxButtonClick = () => {
    captureAmountSetAnalyticEvent({
      amountTokens: limitTokens,
      maxSelected: true,
    });

    setFormValues((currentFormValues) => ({
      ...currentFormValues,
      amountTokens: limitTokens.toFixed(),
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <SwitchChainNotice />

        {isCollateralToggleVisible && (
          <LabeledInlineContent label={t('operationForm.collateral')}>
            <Toggle
              onChange={handleToggleCollateral}
              value={asset.isCollateralOfUser}
              disabled={!isUserConnected || isAccountOnWrongChain || isCollateralToggleDisabled}
            />
          </LabeledInlineContent>
        )}

        {isIntegratedSwapFeatureEnabled ||
        canWrapNativeToken ||
        displayedTokenBalances.length > 1 ? (
          <SelectTokenTextField
            data-testid={TEST_IDS.selectTokenTextField}
            selectedToken={formValues.fromToken}
            value={formValues.amountTokens}
            hasError={!isSubmitting && !!formError && Number(formValues.amountTokens) > 0}
            disabled={
              !isUserConnected ||
              isSubmitting ||
              isApproveFromTokenLoading ||
              formError?.code === 'SUPPLY_CAP_ALREADY_REACHED'
            }
            onChange={(amountTokens) => {
              captureAmountSetAnalyticEvent({
                amountTokens,
                maxSelected: false,
              });

              setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens,
              }));
            }}
            onChangeSelectedToken={(fromToken) =>
              setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens: initialFormValues.amountTokens,
                fromToken,
              }))
            }
            rightMaxButton={{
              label: t('operationForm.rightMaxButtonLabel'),
              onClick: handleRightMaxButtonClick,
            }}
            tokenBalances={displayedTokenBalances}
            displayTokenSourceLabels={!!supplySourceSelection}
            description={
              !isSubmitting && !!formError?.message ? (
                <p className="text-red">{formError.message}</p>
              ) : undefined
            }
          />
        ) : (
          <TokenTextField
            data-testid={TEST_IDS.tokenTextField}
            name="amountTokens"
            token={formValues.fromToken}
            value={formValues.amountTokens}
            onChange={(amountTokens) => {
              captureAmountSetAnalyticEvent({
                amountTokens,
                maxSelected: false,
              });

              setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens,
              }));
            }}
            disabled={
              !isUserConnected ||
              isSubmitting ||
              isApproveFromTokenLoading ||
              formError?.code === 'SUPPLY_CAP_ALREADY_REACHED'
            }
            rightMaxButton={{
              label: t('operationForm.rightMaxButtonLabel'),
              onClick: handleRightMaxButtonClick,
            }}
            hasError={
              isUserConnected && !isSubmitting && !!formError && Number(formValues.amountTokens) > 0
            }
            description={
              isUserConnected && !isSubmitting && !!formError?.message ? (
                <p className="text-red">{formError.message}</p>
              ) : undefined
            }
          />
        )}

        {isUserConnected && (
          <>
            <div className="space-y-2">
              <LabeledInlineContent label={t('operationForm.walletBalance')}>
                {readableFromTokenUserWalletBalanceTokens}
              </LabeledInlineContent>

              <SpendingLimit
                token={formValues.fromToken}
                walletBalanceTokens={fromTokenUserWalletBalanceTokens}
                walletSpendingLimitTokens={fromTokenWalletSpendingLimitTokens}
                onRevoke={revokeFromTokenWalletSpendingLimit}
                isRevokeLoading={isRevokeFromTokenWalletSpendingLimitLoading}
                data-testid={TEST_IDS.spendingLimit}
              />
            </div>

            <Delimiter />
          </>
        )}

        {isUsingBittensorNativeSupplySource && isPositionRouterEnabled && (
          <LabeledInlineContent label={t('operationForm.routerToggle.label')}>
            <Toggle
              onChange={() => setUseLegacyBittensorFlow((current) => !current)}
              value={!useLegacyBittensorFlow}
              disabled={isSubmitting}
            />
          </LabeledInlineContent>
        )}

        {canBundleOpenPosition && borrowTargetAsset && (
          <>
            <LabeledInlineContent
              label={t('operationForm.openPositionToggle.label')}
              tooltip={t('operationForm.openPositionToggle.tooltip', {
                tokenSymbol: borrowTargetAsset.vToken.underlyingToken.symbol,
              })}
            >
              <Toggle
                onChange={() => setBundleBorrowEnabled((current) => !current)}
                value={bundleBorrowEnabled}
                disabled={isSubmitting}
              />
            </LabeledInlineContent>

            {bundleBorrowEnabled && (
              <TokenTextField
                name="bundleBorrowAmountTokens"
                token={borrowTargetAsset.vToken.underlyingToken}
                value={bundleBorrowAmountTokens}
                onChange={setBundleBorrowAmountTokens}
                disabled={!isUserConnected || isSubmitting}
                rightMaxButton={{
                  label: t('operationForm.safeMaxButtonLabel'),
                  onClick: handleBundleBorrowSafeMaxClick,
                }}
              />
            )}

            {bundleBorrowExceedsLimit && (
              <NoticeError
                description={t('operationForm.error.exceedsBundledBorrowLimit', {
                  max: formatTokensToReadableValue({
                    value: maxBundledBorrowTokens,
                    token: borrowTargetAsset.vToken.underlyingToken,
                  }),
                })}
              />
            )}

            {!bundleBorrowExceedsLimit &&
              bundleBorrowExceedsCash &&
              borrowTargetAvailableTokens && (
                <NoticeWarning
                  description={t('operationForm.error.higherThanAvailableLiquidityLive', {
                    available: formatTokensToReadableValue({
                      value: borrowTargetAvailableTokens,
                      token: borrowTargetAsset.vToken.underlyingToken,
                    }),
                  })}
                />
              )}

            {!bundleBorrowExceedsLimit &&
              !bundleBorrowExceedsCash &&
              bundleBorrowExceedsSafeMax && (
                <NoticeWarning
                  description={t('operationForm.warning.exceedsBundledBorrowSafeMax', {
                    safeMax: formatTokensToReadableValue({
                      value: safeMaxBundledBorrowTokens,
                      token: borrowTargetAsset.vToken.underlyingToken,
                    }),
                  })}
                />
              )}
          </>
        )}

        <TransactionQueue
          items={
            bundleBorrowEnabled
              ? routerOpenPositionQueueItems
              : useRouterFlow
                ? routerSupplyQueueItems
                : bittensorSupplyQueueItems
          }
        />

        <Footer
          analyticVariant="supply_form"
          balanceMutations={balanceMutations}
          pool={pool}
          submitButtonLabel={t('operationForm.submitButtonLabel.supply')}
          isFormValid={
            isFormValid &&
            !bundleBorrowExceedsCash &&
            !bundleBorrowAmountMissing &&
            !bundleBorrowExceedsLimit
          }
          approval={approval}
          simulatedPool={simulatedPool}
          swapFromToken={isUsingSwap ? formValues.fromToken : undefined}
          swapToToken={isUsingSwap ? asset.vToken.underlyingToken : undefined}
          swapQuote={swapQuote}
          isUserAcknowledgingHighPriceImpact={formValues.acknowledgeHighPriceImpact}
          setAcknowledgeHighPriceImpact={(acknowledgeHighPriceImpact) =>
            setFormValues((currentFormValues) => ({
              ...currentFormValues,
              acknowledgeHighPriceImpact,
            }))
          }
          isLoading={isSubmitting || isGetSwapQuoteLoading}
        />
      </div>
    </form>
  );
};

export default SupplyForm;
